import { useFieldArray, useFormContext } from 'react-hook-form';
import { faPlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';

import { PlatformAppUiSchema } from '@typings';
import { isSchemaField } from '@utils/guards';

import { Button, Icon, Render } from '@components/Shared';

import { PlatformAppsUiSchema } from './PlatformAppsUiSchema';

const copySchema = (
  schema: PlatformAppUiSchema.Model,
): PlatformAppUiSchema.Model => JSON.parse(JSON.stringify(schema));

type ArrayItem = {
  id: string;
};

type Props = {
  schema: PlatformAppUiSchema.Array;
};

export const PlatformAppUiSchemaArray: React.FC<Props> = ({
  schema: { title, description, name, children },
}) => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const updateModelName = (model: PlatformAppUiSchema.Model, index: number) => {
    const schema = copySchema(model);

    if (isSchemaField(schema)) {
      schema.properties.name = schema.properties.name.replace(
        `${name}.$index`,
        `${name}.${index}`,
      );
    } else {
      schema.children = schema.children.map((schema) =>
        updateModelName(schema, index),
      );
    }

    return schema;
  };

  const makeItem = ({ id }: ArrayItem, index: number) => {
    const normalizedNodes = children.map((schema) =>
      updateModelName(schema, index),
    );

    return (
      <div
        key={id}
        className="flex flex-col gap-6 rounded-xl border border-neural-02 p-6"
      >
        {normalizedNodes.map((schema) => (
          <PlatformAppsUiSchema key={`${schema.name}.${id}`} schema={schema} />
        ))}
        <div className="flex justify-end">
          <Button
            variant="ghost-error"
            icon={faTrashAlt}
            className="capitalize"
            onClick={() => remove(index)}
          >
            Delete entry
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-6">
      <p className="flex items-start text-h6">{title}</p>
      <Render if={description}>
        <p className="mt-0.5 text-footnote text-neural-04">{description}</p>
      </Render>
      <div className="mt-4 flex flex-col gap-4">{fields.map(makeItem)}</div>
      <div className="mt-4 flex justify-end">
        <Button className="capitalize" onClick={() => append('')}>
          <Icon icon={faPlus} />
          Add entry
        </Button>
      </div>
    </div>
  );
};
